// ReportCard — card view for a single report
import type { ReportItem } from "@/mocks/relatorios";
import ReportStatusBadge from "./ReportStatusBadge";
import ReportTypeBadge from "./ReportTypeBadge";

const typeIcons: Record<string, string> = {
  "Técnico": "ri-file-list-3-line",
  "Executivo": "ri-government-line",
  "Fotográfico": "ri-camera-line",
  "Cartográfico/GIS": "ri-map-2-line",
  "Inventário arbóreo": "ri-tree-line",
  "FCA individual": "ri-file-text-line",
  "Árvores de risco": "ri-alert-line",
  "Manejo recomendado": "ri-scissors-line",
  "Espécies": "ri-leaf-line",
  "Bairro/logradouro": "ri-map-pin-line",
  "Área verde": "ri-landscape-line",
  "Unidade de Conservação": "ri-shield-star-line",
  "Carbono": "ri-cloud-line",
  "Compensação ambiental": "ri-plant-line",
  "Documental": "ri-folder-line",
  "RRR / Governança": "ri-shield-check-line",
  "Auditoria": "ri-search-line",
  "Transparência pública": "ri-eye-line",
};

interface Props {
  report: ReportItem;
  onView: (r: ReportItem) => void;
}

export default function ReportCard({ report, onView }: Props) {
  const icon = typeIcons[report.type] || "ri-file-line";
  return (
    <div className="bg-white rounded-xl border border-background-200/70 p-4 hover:border-primary-200 transition-colors cursor-pointer group" onClick={() => onView(report)}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
          <i className={`${icon} text-primary-500 w-full h-full flex items-center justify-center text-lg`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-semibold text-foreground-900 text-sm leading-snug line-clamp-2">{report.title}</h4>
          </div>
          <p className="text-xs text-foreground-400 mt-1 line-clamp-2">{report.description}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <ReportTypeBadge type={report.type} />
        <ReportStatusBadge status={report.status} />
        <span className="text-xs font-medium text-accent-600 bg-accent-50 px-2 py-0.5 rounded">{report.format}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-foreground-400">
        <span className="truncate mr-2">{report.projectName}</span>
        <span className="whitespace-nowrap">{report.generatedAt || "Pendente"}</span>
      </div>
      <div className="flex items-center gap-1 mt-3 pt-3 border-t border-background-100">
        <button
          onClick={(e) => { e.stopPropagation(); onView(report); }}
          className="flex-1 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap text-center"
        >
          <i className="ri-eye-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
          Visualizar
        </button>
        <button
          onClick={(e) => e.stopPropagation()}
          className="flex-1 px-3 py-1.5 text-xs font-medium text-accent-600 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap text-center"
        >
          <i className="ri-download-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
          Baixar
        </button>
        <button
          onClick={(e) => e.stopPropagation()}
          className="flex-1 px-3 py-1.5 text-xs font-medium text-secondary-600 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors whitespace-nowrap text-center"
        >
          <i className="ri-refresh-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
          Gerar novamente
        </button>
      </div>
    </div>
  );
}